import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Card, CardContent, CircularProgress, Typography, Alert } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../AuthContext";
import useAuthenticatedFetch from "../hooks/useAuthenticatedFetch";
import ProRequestForm from "../components/billing/ProRequestForm";

export default function BillingPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const fetchWithAuth = useAuthenticatedFetch();

  const communityId = user?.communityId;
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    if (!communityId) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetchWithAuth(`${process.env.REACT_APP_BASE_URL}/billing/status/${communityId}`);
      const json = await res.json().catch(() => null);
      if (!res.ok) throw new Error(json?.error || json?.message || t("billing.errors.loadStatus"));
      setStatus(json);
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [communityId, fetchWithAuth, t]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const planLabel = useMemo(() => {
    if (!status) return "-";
    return status.proActive ? t("billing.plan.pro") : t("billing.plan.free");
  }, [status, t]);

  const proUntilLabel = useMemo(() => {
    if (!status?.currentPeriodEnd) return t("billing.proUntilUnlimited");
    return new Intl.DateTimeFormat(i18n.language || undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    }).format(new Date(status.currentPeriodEnd));
  }, [i18n.language, status?.currentPeriodEnd, t]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <CircularProgress size={20} />
        <Typography>{t("billing.loading")}</Typography>
      </Box>
    );
  }

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h5" sx={{ mb: 2 }}>
          {t("billing.title")}
        </Typography>

        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

        <Typography variant="body1" sx={{ mb: 1 }}>
          {t("billing.currentPlan")} <b>{planLabel}</b>
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("billing.statusLine", { status: status?.subscriptionStatus || "-" })}
        </Typography>
        {status?.proActive ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("billing.proUntil")} <b>{proUntilLabel}</b>
          </Typography>
        ) : null}

        {!status?.proActive ? (
          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t("billing.proRequest.pageIntro")}
            </Typography>
            <ProRequestForm communityId={communityId} onSubmitted={refresh} />
          </Box>
        ) : (
          <Alert severity="success" sx={{ mt: 2, mb: 2 }}>
            {t("billing.alreadyPro")}
          </Alert>
        )}

        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button variant="outlined" onClick={refresh}>
            {t("billing.cta.refresh")}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}

